import React, {
	useRef, useState, useEffect, useCallback
} from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import customToast from '../custom-toast.js';
import { getErrorMessage } from '../../../utils/util.js';
import { getDocument } from '../../../Services/api-config.js';
import DocumentHeader from './document-header.js';
import { FetchingMorePages, InitialLoading } from './loading-state.js';
import PageRenderer from './page-renderer.js';

const extractNumber = (value) => {
	const match = value?.toString().match(/\d+/);
	return match ? Number(match[0]) : 1;
};

const DocumentViewer = ({
	data,
	docName,
	showDetails = false,
	isMobile,
	documentLoading,
	setDocumentLoading,
	close,
	divHeightVal,
	isEsignFlow = false,
	showOptions = true,
	setNumPages = () => {},
	setPageNumber = () => {},
	showSign = false,
	file
}) => {
	const nextPageNumberRef = useRef(2);
	const pdfContainerRef = useRef(null);
	const pendingPagesRef = useRef(new Set());
	const pageRefs = useRef([]);
	const [pages, setPages] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [combinedDoc, setCombinedDoc] = useState(null);
	const [totalPages, setTotalPages] = useState(0);
	const [isFetching, setIsFetching] = useState(false);
	const { showToast } = customToast();

	const numPages = extractNumber(data?.totalStamps);
	useEffect(() => {
		if (!data?.stampPaperPdfPath) {
			return;
		}

		if (isEsignFlow) {
			// For isEsignFlow true, set the combined PDF once
			setCombinedDoc(data.stampPaperPdfPath);
		} else {
			// For isEsignFlow false, work with individual pages
			setPages([data.stampPaperPdfPath]);
		}

		setCurrentPage(1);
		setDocumentLoading(false);
	}, [data?.stampPaperPdfPath, isEsignFlow, setDocumentLoading]);

	const fetchNextPage = useCallback(async () => {
		const nextPageNumber = nextPageNumberRef.current;

		if (
			pages.length >= numPages
			|| pendingPagesRef.current.has(nextPageNumber)
		) {
			return;
		}

		pendingPagesRef.current.add(nextPageNumber);
		setIsFetching(true);

		try {
			const response = await getDocument(data?.stampOrderId, nextPageNumber);
			if (response?.status === 200 && response.data?.pdfPage) {
				setPages((prevPages) => [...prevPages, response.data.pdfPage]);
				nextPageNumberRef.current += 1;
			} else {
				showToast('error', getErrorMessage(response));
			}
		} catch (error) {
			showToast('error', getErrorMessage(error));
		} finally {
			pendingPagesRef.current.delete(nextPageNumber);
			setIsFetching(false);
		}
	}, [pages.length, numPages, data?.stampOrderId, showToast]);

	useEffect(() => {
		const container = pdfContainerRef.current;
		if (!container) {
			return;
		}

		const handleScroll = () => {
			let currentVisiblePage = 1;

			for (const [index, ref] of pageRefs.current.entries()) {
				if (ref && ref.getBoundingClientRect().top < window.innerHeight * 0.5) {
					currentVisiblePage = index + 1;
				}
			}

			setCurrentPage(currentVisiblePage);
			setPageNumber(currentVisiblePage);
			if (
				!isEsignFlow
				&& container.scrollTop + container.clientHeight
					>= container.scrollHeight - 50
				&& !isFetching
				&& pages.length < numPages
			) {
				fetchNextPage();
			}
		};

		container.addEventListener('scroll', handleScroll);

		if (!isEsignFlow) {
			const checkPDFHeight = () => {
				const totalPDFHeight = pageRefs.current.reduce(
					(total, page) => total + (page?.offsetHeight || 0),
					0
				);

				if (
					totalPDFHeight <= container.clientHeight
					&& !isFetching
					&& pages.length < numPages
				) {
					fetchNextPage();
				}
			};

			// Give time for PDF pages to render
			const timeoutId = setTimeout(checkPDFHeight, 500);

			return () => {
				clearTimeout(timeoutId);
				container.removeEventListener('scroll', handleScroll);
			};
		}
	}, [fetchNextPage, isFetching, numPages, pages.length, isEsignFlow]);

	const onDocumentLoadSuccess = ({ numPages }) => {
		setTotalPages(numPages);
		setNumPages(numPages);
	};

	const getMaxHeight = () => {
		if (!isMobile) {
			return '72vh';
		}

		return divHeightVal ? `${divHeightVal * 0.7}px` : '65vh';
	};

	return (
		<Box position='relative'>
			<DocumentHeader
				close={close}
				combinedDoc={combinedDoc}
				currentPage={currentPage}
				data={data}
				docName={docName}
				isEsignFlow={isEsignFlow}
				isMobile={isMobile}
				numPages={numPages}
				setDocumentLoading={setDocumentLoading}
				showDetails={showDetails}
				showOptions={showOptions}
				totalPages={totalPages}
			/>

			<div
				ref={pdfContainerRef}
				style={{
					overflowY: 'auto',
					maxHeight: getMaxHeight(),
					maxWidth: '600px',
					scrollBehavior: 'smooth'
				}}
				className='popUp-pdf-content custom-scrollbar'
			>
				{documentLoading && <InitialLoading />}

				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						position: 'relative'
					}}
				>
					<PageRenderer
						combinedDoc={combinedDoc}
						isEsignFlow={isEsignFlow}
						onDocumentLoadSuccess={onDocumentLoadSuccess}
						pageRefs={pageRefs}
						pages={pages}
						totalPages={totalPages}
						showSign={showSign}
						file={file}
					/>
				</div>
			</div>
			<FetchingMorePages isVisible={isFetching && !isEsignFlow} />
		</Box>
	);
};

DocumentViewer.propTypes = {
	data: PropTypes.any,
	docName: PropTypes.string,
	showDetails: PropTypes.bool,
	isMobile: PropTypes.bool,
	divHeightVal: PropTypes.number,
	documentLoading: PropTypes.bool,
	setDocumentLoading: PropTypes.func,
	close: PropTypes.func,
	isEsignFlow: PropTypes.bool,
	showOptions: PropTypes.bool,
	setNumPages: PropTypes.func,
	setPageNumber: PropTypes.func,
	showSign: PropTypes.bool,
	file: PropTypes.any
};

export default DocumentViewer;
